import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BarChartDate from "../../chart/BarChartDate";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { IconView } from "../../assets/icon/metric/IconView";
import { IconLike } from "../../assets/icon/metric/IconLike";
import { IconReply } from "../../assets/icon/metric/IconReply";
import { IconRepost } from "../../assets/icon/metric/IconRepost";
import { IconUsers } from "../../assets/icon/metric/IconUsers";
import { IconThreads } from "../../assets/icon/metric/IconThreads";
import { IconFollower } from "../../assets/icon/metric/IconFollower";
import numeral from "numeral";
import CountUp from "react-countup";

const StatusTitle = ({ type, isFullWidth, isMobile }) => {
	const { t } = useTranslation();
	const theme = useTheme();

	return (
		<Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: isFullWidth ? "100%" : "auto" }}>
			<Box sx={{ padding: theme.spacing(0.25), backgroundColor: theme.palette.primary.main, color: theme.palette.background.paper, borderRadius: "4px" }}>
				{type === "profileVisit" && <IconUsers size={16} />}
				{type === "view" && <IconView size={16} />}
				{type === "like" && <IconLike size={16} />}
				{type === "reply" && <IconReply size={16} />}
				{type === "repost" && <IconRepost size={16} />}
				{type === "threads" && <IconThreads size={16} />}
				{type === "follower" && <IconFollower size={16} />}
			</Box>
			<Typography variant="body1" sx={{ fontWeight: 700 }}>
				{type === "profileVisit" && t("common.profileView")}
				{type === "view" && t("common.views")}
				{type === "like" && t("common.likes")}
				{type === "reply" && t("common.replies")}
				{type === "repost" && t("common.reposts")}
				{type === "threads" && t("common.threads")}
				{type === "follower" && t("common.followers")}
			</Typography>
		</Stack>
	);
};

const MemoizedCountUp = React.memo(
	({ value }) => (
		<CountUp
			key={value}
			preserveValue={true}
			end={value}
			duration={0.5}
			separator=","
			formattingFn={(val) =>
				numeral(val).format(val < 1000 || val % 1000 < 100 ? "0a" : "0.0a")
			}
		/>
	),
	(prevProps, nextProps) => {
		return prevProps.value === nextProps.value;
	}
);

export const CardStatusCompare = ({
	type,
	value1,
	value2,
	monthlyViews,
	setPostLoading,
	isMobile,
}) => {
	const containerRef = useRef(null);
	const [containerWidth, setContainerWidth] = useState(0);

	useEffect(() => {
		const updateContainerWidth = () => {
			if (containerRef.current) {
				setContainerWidth(containerRef.current.offsetWidth);
			}
		};

		// Use ResizeObserver to handle size changes
		const resizeObserver = new ResizeObserver(() => {
			updateContainerWidth();
		});

		if (containerRef.current) {
			resizeObserver.observe(containerRef.current);
		}

		// Cleanup function to disconnect the observer
		return () => {
			resizeObserver.disconnect();
		};
	}, [containerRef]);

	return (
		<Stack
			direction="column"
			spacing={isMobile ? 2 : 6}
			sx={{ position: "relative", width: "100%" }}
		>
			<StatusTitle type={type} />
			<Box>
				<Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 700 }}>
					{!setPostLoading && (
						<>
							<MemoizedCountUp value={value1} />
							{" / "}
							<MemoizedCountUp value={value2} />
						</>
					)}
				</Typography>
			</Box>
		</Stack>
	);
};
